import { useState } from 'react';
import { Pencil, Plus, Tag, Trash2 } from 'lucide-react';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import NoItems from '@/components/ui/NoItems.tsx';
import {
  SectionLayout,
  SectionLayoutSkeleton,
} from '@/components/ui/SectionLayout.tsx';
import {
  SettingsList,
  SettingsListRow,
} from '@/components/ui/SettingsList.tsx';
import { useConfirmationDialogContext } from '@/components/ui/ConfirmDialog.Context.tsx';
import { TagWebDto } from '@/features/dashboard/tags/types/TagWebDto.ts';
import useGetTags from '@/features/dashboard/tags/hooks/useGetTags.ts';
import useDeleteTag from '@/features/dashboard/tags/hooks/useDeleteTag.ts';
import TagChip from '@/features/dashboard/tags/components/TagChip.tsx';
import CreateTagModal from '@/features/dashboard/tags/components/CreateTagModal.tsx';
import EditTagModal from '@/features/dashboard/tags/components/EditTagModal.tsx';

const SECTION_DESCRIPTION =
  'Tagi są wspólne dla całej firmy — te same przypisujesz uczestnikom, klientom, wyjazdom i wpłatom. Dodanie istniejącej nazwy nie tworzy duplikatu.';

const TagsList = () => {
  const { data: tags, isLoading } = useGetTags();
  const deleteTag = useDeleteTag();
  const { showDialog } = useConfirmationDialogContext();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<TagWebDto | null>(null);

  const handleDelete = async (tag: TagWebDto) => {
    if (!tag.id) return;
    const confirmed = await showDialog({
      title: 'Usuń tag',
      description: `Czy na pewno chcesz usunąć tag „${tag.name}"? Zostanie on odpięty od wszystkich obiektów.`,
      variant: 'destructive',
      confirmText: 'Usuń',
    });
    if (confirmed) {
      deleteTag.mutate(tag.id);
    }
  };

  if (isLoading) {
    return <SectionLayoutSkeleton rows={3} />;
  }

  const hasTags = !!tags && tags.length > 0;

  return (
    <>
      <SectionLayout title="Tagi" description={SECTION_DESCRIPTION}>
        {hasTags ? (
          <>
            <SettingsList>
              {tags.map((tag) => (
                <SettingsListRow
                  key={tag.id}
                  leading={<TagChip tag={tag} />}
                  // Nazwa jest już w chipie — powtórzona zwykłym tekstem
                  // czytałaby się jak druga, inna wartość.
                  title={null}
                  actions={
                    <>
                      <ActionButton
                        tone="outline"
                        size="toolbar"
                        onClick={() => setEditingTag(tag)}
                      >
                        <Pencil />
                        Zmień
                      </ActionButton>
                      <ActionButton
                        tone="danger"
                        size="toolbar"
                        iconOnly
                        title="Usuń tag"
                        onClick={() => handleDelete(tag)}
                      >
                        <Trash2 />
                      </ActionButton>
                    </>
                  }
                />
              ))}
            </SettingsList>

            <ActionButton
              tone="outline"
              size="toolbar"
              className="mt-3"
              onClick={() => setIsCreateModalOpen(true)}
            >
              <Plus />
              Dodaj tag
            </ActionButton>
          </>
        ) : (
          <NoItems
            icon={<Tag />}
            title="Nie masz jeszcze tagów"
            description="Dodaj pierwszy tag, aby oznaczać uczestników, klientów i wyjazdy."
          >
            <ActionButton
              tone="outline"
              size="toolbar"
              onClick={() => setIsCreateModalOpen(true)}
            >
              <Plus />
              Dodaj tag
            </ActionButton>
          </NoItems>
        )}
      </SectionLayout>

      {isCreateModalOpen && (
        <CreateTagModal onClose={() => setIsCreateModalOpen(false)} />
      )}

      {editingTag && (
        <EditTagModal tag={editingTag} onClose={() => setEditingTag(null)} />
      )}
    </>
  );
};

export default TagsList;
